import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  sendContactEmailWithRateLimit,
  validateContactForm,
  sanitizeInput,
} from '../../../services/emailService';
import './Contact.css';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Sanitize form data before validation and submission
    const sanitizedData = {
      name: sanitizeInput(formData.name),
      email: sanitizeInput(formData.email),
      subject: sanitizeInput(formData.subject),
      message: sanitizeInput(formData.message),
    };

    // Validate form with sanitized data
    const validation = validateContactForm(sanitizedData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      toast.error(t('contact.errors.fixErrors'), {
        position: i18n.language === 'ar' ? 'top-left' : 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Send email with sanitized data
      const result = await sendContactEmailWithRateLimit(sanitizedData);

      if (result.success) {
        // Show custom success toast
        setShowSuccessToast(true);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
        });
        setErrors({});

        // Hide custom toast after 5 seconds
        setTimeout(() => {
          setShowSuccessToast(false);
        }, 5000);
      } else {
        // Failed
        toast.error(result.message, {
          position: i18n.language === 'ar' ? 'top-left' : 'top-right',
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(t('contact.errors.sendFailed'), {
        position: i18n.language === 'ar' ? 'top-left' : 'top-right',
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        {/* Custom Success Toast */}
        <AnimatePresence>
          {showSuccessToast && (
            <motion.div
              className="custom-success-toast"
              initial={{ opacity: 0, y: -100, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.8 }}
              transition={{ 
                type: "spring", 
                stiffness: 300, 
                damping: 25 
              }}
              dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            >
              <motion.div 
                className="toast-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {/* Animated Success Icon */}
                <div className="toast-icon-wrapper">
                  <motion.div
                    className="toast-icon-circle"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 200, 
                      damping: 15,
                      delay: 0.1 
                    }}
                  >
                    <motion.svg 
                      viewBox="0 0 52 52" 
                      className="toast-checkmark"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <motion.circle
                        cx="26"
                        cy="26"
                        r="24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                      />
                      <motion.path
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        d="M14 27l7 7 16-16"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.3, delay: 0.5 }}
                      />
                    </motion.svg>
                  </motion.div>
                  
                  {/* Pulse Rings */}
                  <motion.div
                    className="pulse-ring"
                    animate={{
                      scale: [1, 1.8],
                      opacity: [0.8, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeOut"
                    }}
                  />
                  <motion.div
                    className="pulse-ring"
                    animate={{
                      scale: [1, 1.8],
                      opacity: [0.8, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: 0.5,
                      ease: "easeOut"
                    }}
                  />
                </div>

                {/* Toast Text */}
                <div className="toast-text">
                  <motion.h4
                    initial={{ opacity: 0, x: i18n.language === 'ar' ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    {t('contact.success.title')}
                  </motion.h4>
                  <motion.p
                    initial={{ opacity: 0, x: i18n.language === 'ar' ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    {t('contact.success.message')}
                  </motion.p>
                </div>

                {/* Close Button */}
                <motion.button
                  className="toast-close"
                  onClick={() => setShowSuccessToast(false)}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  ×
                </motion.button>
              </motion.div>

              {/* Confetti Particles */}
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  className="confetti-particle"
                  style={{
                    left: `${20 + (i * 60 / 12)}%`,
                    background: ['#ff4c4c', '#4e5fce', '#ffd1a4'][i % 3],
                  }}
                  initial={{ y: 0, opacity: 0, scale: 0 }}
                  animate={{
                    y: [0, -100, 200],
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0.5],
                    rotate: [0, 180, 360],
                  }}
                  transition={{
                    duration: 2,
                    delay: 0.3 + (i * 0.05),
                    ease: "easeOut"
                  }}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          className="contact-content"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="section-tag">{t('contact.tag')}</div>
          <h2>
            {t('contact.title')}{' '}
            <span className="gradient-text">{t('contact.titleHighlight')}</span>
          </h2>
          <p>{t('contact.subtitle')}</p>

          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-field">
                <motion.input
                  type="text"
                  name="name"
                  placeholder={t('contact.form.namePlaceholder')}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  whileFocus={{ scale: 1.02 }}
                  className={errors.name ? 'error' : ''}
                  disabled={isSubmitting}
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                />
                {errors.name && <span className="error-message">{t('contact.errors.nameRequired')}</span>}
              </div>

              <div className="form-field">
                <motion.input
                  type="email"
                  name="email"
                  placeholder={t('contact.form.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  whileFocus={{ scale: 1.02 }}
                  className={errors.email ? 'error' : ''}
                  disabled={isSubmitting}
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                />
                {errors.email && <span className="error-message">{t('contact.errors.emailInvalid')}</span>}
              </div>
            </div>

            <div className="form-field">
              <motion.input
                type="text"
                name="subject"
                placeholder={t('contact.form.subjectPlaceholder')}
                value={formData.subject}
                onChange={handleChange}
                whileFocus={{ scale: 1.02 }}
                className={errors.subject ? 'error' : ''}
                disabled={isSubmitting}
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              />
              {errors.subject && <span className="error-message">{t('contact.errors.subjectTooLong')}</span>}
            </div>

            <div className="form-field">
              <motion.textarea
                name="message"
                placeholder={t('contact.form.messagePlaceholder')}
                rows="6"
                value={formData.message}
                onChange={handleChange}
                required
                whileFocus={{ scale: 1.02 }}
                className={errors.message ? 'error' : ''}
                disabled={isSubmitting}
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              />
              {errors.message && <span className="error-message">{t('contact.errors.messageRequired')}</span>}
            </div>

            <motion.button
              type="submit"
              className="btn-primary"
              whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
              disabled={isSubmitting}
              dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  {t('contact.form.sending')}
                </>
              ) : (
                <>{t('contact.form.sendButton')}</>
              )}
            </motion.button>

            <p className="form-note">
              {t('contact.form.requiredNote')}
            </p>
          </form>

          {/* Contact Info */}
          <div className="contact-info">
            <motion.div
              className="contact-info-item"
              whileHover={{ scale: 1.05 }}
            >
              <div className="contact-icon">📧</div>
              <div className="contact-details">
                <h4>{t('contact.info.email')}</h4>
                <a href="mailto:orvin.contact@gmail.com">orvin.contact@gmail.com</a>
              </div>
            </motion.div>

            {/* Uncomment if you want phone and location */}
            {/* 
            <motion.div
              className="contact-info-item"
              whileHover={{ scale: 1.05 }}
            >
              <div className="contact-icon">📱</div>
              <div className="contact-details">
                <h4>{t('contact.info.phone')}</h4>
                <p>+1 (555) 123-4567</p>
              </div>
            </motion.div>

            <motion.div
              className="contact-info-item"
              whileHover={{ scale: 1.05 }}
            >
              <div className="contact-icon">📍</div>
              <div className="contact-details">
                <h4>{t('contact.info.location')}</h4>
                <p>Trieste, Italy</p>
              </div>
            </motion.div>
            */}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;